import React, { useState, useEffect } from 'react';
import { 
  CloudIcon,
  CircleStackIcon,
  DocumentIcon,
  ServerStackIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

export default function DataSourceSelector({ onDataSourceSelect, selectedDataSource }) {
  const [sourceType, setSourceType] = useState('');
  const [existingConnections, setExistingConnections] = useState([]);
  const [showNewSourceForm, setShowNewSourceForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [newSourceConfig, setNewSourceConfig] = useState({
    name: '',
    connector_type: '',
    source_config: {},
    auth_config: {}
  });

  // Load existing connections on mount
  useEffect(() => {
    loadExistingConnections();
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadExistingConnections = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/connectors', { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setExistingConnections(data.connectors || []);
      }
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceTypeSelect = (type) => {
    setSourceType(type);
    setNewSourceConfig({
      name: '',
      connector_type: type,
      source_config: getDefaultSourceConfig(type),
      auth_config: getDefaultAuthConfig(type)
    });
    setShowNewSourceForm(true);
  };

  const getDefaultSourceConfig = (type) => {
    switch (type) {
      case 's3':
        return {
          bucket_name: '',
          prefix: '',
          file_pattern: '**/*.pdf'
        };
      case 'mongodb':
        return {
          database: '',
          collection: '',
          query_filter: '{}'
        };
      case 'local':
        return {
          directory_path: '',
          recursive: true
        };
      default:
        return {};
    }
  };

  const getDefaultAuthConfig = (type) => {
    switch (type) {
      case 's3':
        return {
          method: 'iam_access_keys',
          access_key_id: '',
          secret_access_key: '',
          region: 'us-east-1'
        };
      case 'mongodb':
        return {
          method: 'username_password',
          connection_string: '',
          username: '',
          password: ''
        };
      case 'local':
        return {};
      default:
        return {};
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    
    try {
      const response = await fetch('/api/v1/connectors/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(newSourceConfig)
      });
      
      const result = await response.json();
      setTestResult(result);
      
    } catch (err) {
      setTestResult({
        success: false,
        error: err.message || 'Connection test failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveConnection = async () => {
    try {
      const response = await fetch('/api/v1/connectors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(newSourceConfig)
      });
      
      if (response.ok) {
        const data = await response.json();
        onDataSourceSelect(data.connector);
        setShowNewSourceForm(false);
        loadExistingConnections(); // Refresh list
      }
    } catch (err) {
      console.error('Failed to save connection:', err);
    }
  };

  const renderSourceIcon = (type) => {
    switch (type) {
      case 's3':
        return <CloudIcon className="w-6 h-6" />;
      case 'mongodb':
        return <CircleStackIcon className="w-6 h-6" />;
      case 'local':
        return <DocumentIcon className="w-6 h-6" />;
      default:
        return <ServerStackIcon className="w-6 h-6" />;
    }
  };

  const renderSourceConfig = () => {
    if (!showNewSourceForm) return null;

    return (
      <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4">Configure New Data Source</h4>
        
        {/* Connection name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Connection Name
          </label>
          <input
            type="text"
            placeholder="e.g., Legal Documents S3 Bucket"
            value={newSourceConfig.name}
            onChange={(e) => setNewSourceConfig({
              ...newSourceConfig,
              name: e.target.value
            })}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* S3 Configuration */}
        {sourceType === 's3' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  S3 Bucket Name
                </label>
                <input
                  type="text"
                  value={newSourceConfig.source_config.bucket_name}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    source_config: {
                      ...newSourceConfig.source_config,
                      bucket_name: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prefix (Optional)
                </label>
                <input
                  type="text"
                  placeholder="documents/legal/"
                  value={newSourceConfig.source_config.prefix}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    source_config: {
                      ...newSourceConfig.source_config,
                      prefix: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* S3 Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Key ID
                </label>
                <input
                  type="text"
                  value={newSourceConfig.auth_config.access_key_id}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    auth_config: {
                      ...newSourceConfig.auth_config,
                      access_key_id: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret Access Key
                </label>
                <input
                  type="password"
                  value={newSourceConfig.auth_config.secret_access_key}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    auth_config: {
                      ...newSourceConfig.auth_config,
                      secret_access_key: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <select
                  value={newSourceConfig.auth_config.region}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    auth_config: {
                      ...newSourceConfig.auth_config,
                      region: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">Europe (Ireland)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* MongoDB Configuration */}
        {sourceType === 'mongodb' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Database Name
                </label>
                <input
                  type="text"
                  value={newSourceConfig.source_config.database}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    source_config: {
                      ...newSourceConfig.source_config,
                      database: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Collection Name
                </label>
                <input
                  type="text"
                  value={newSourceConfig.source_config.collection}
                  onChange={(e) => setNewSourceConfig({
                    ...newSourceConfig,
                    source_config: {
                      ...newSourceConfig.source_config,
                      collection: e.target.value
                    }
                  })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Connection String
              </label>
              <input
                type="text"
                placeholder="mongodb://username:password@host:port/database"
                value={newSourceConfig.auth_config.connection_string}
                onChange={(e) => setNewSourceConfig({
                  ...newSourceConfig,
                  auth_config: {
                    ...newSourceConfig.auth_config,
                    connection_string: e.target.value
                  }
                })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        )}

        {/* Local Files Configuration */}
        {sourceType === 'local' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Directory Path
            </label>
            <input
              type="text"
              placeholder="/path/to/documents"
              value={newSourceConfig.source_config.directory_path}
              onChange={(e) => setNewSourceConfig({
                ...newSourceConfig,
                source_config: {
                  ...newSourceConfig.source_config,
                  directory_path: e.target.value
                }
              })}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Test connection */}
        <div className="flex items-center space-x-4 mt-4">
          <button
            onClick={handleTestConnection}
            disabled={testingConnection || !newSourceConfig.name}
            className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {testingConnection ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </button>
          
          <button
            onClick={() => {
              setShowNewSourceForm(false);
              setTestResult(null);
            }}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`mt-4 p-3 rounded-md ${
            testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center">
              {testResult.success ? (
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
              ) : (
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
              )}
              <span className={`text-sm font-medium ${
                testResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {testResult.success ? 'Connection successful!' : 'Connection failed'}
              </span>
            </div>
            {testResult.error && (
              <p className="text-sm text-red-700 mt-1">{testResult.error}</p>
            )}
            {testResult.success && (
              <div className="mt-2">
                <button
                  onClick={handleSaveConnection}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Save & Use This Connection
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Existing connections */}
      {existingConnections.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Use Existing Connection</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {existingConnections.map((conn) => (
              <button
                key={conn.id}
                onClick={() => onDataSourceSelect(conn)}
                className={`p-4 border rounded-lg text-left transition-all ${
                  selectedDataSource?.id === conn.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center mb-2">
                  {renderSourceIcon(conn.connector_type)}
                  <span className="ml-2 font-medium text-gray-900">{conn.name}</span>
                  {selectedDataSource?.id === conn.id && (
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 ml-auto" />
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {conn.connector_type.toUpperCase()} • {conn.source_config.bucket_name || conn.source_config.database || 'Local'}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Last used: {conn.last_run ? new Date(conn.last_run).toLocaleDateString() : 'Never'}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Or create new */}
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Or Create New Connection</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => handleSourceTypeSelect('s3')}
            className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
          >
            <div className="flex flex-col items-center text-center">
              <CloudIcon className="w-8 h-8 text-blue-500 mb-2" />
              <span className="font-medium text-gray-900">AWS S3</span>
              <span className="text-sm text-gray-500">Cloud storage bucket</span>
            </div>
          </button>

          <button
            onClick={() => handleSourceTypeSelect('mongodb')}
            className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
          >
            <div className="flex flex-col items-center text-center">
              <CircleStackIcon className="w-8 h-8 text-green-500 mb-2" />
              <span className="font-medium text-gray-900">MongoDB</span>
              <span className="text-sm text-gray-500">Document database</span>
            </div>
          </button>

          <button
            onClick={() => handleSourceTypeSelect('local')}
            className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
          >
            <div className="flex flex-col items-center text-center">
              <DocumentIcon className="w-8 h-8 text-purple-500 mb-2" />
              <span className="font-medium text-gray-900">Local Files</span>
              <span className="text-sm text-gray-500">File system directory</span>
            </div>
          </button>
        </div>
      </div>

      {renderSourceConfig()}

      {/* Help text */}
      <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
        <div className="flex items-start">
          <InformationCircleIcon className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Choose your document source:</strong> Select where your documents are stored. 
            The system will apply your discovery tags to all documents in the selected source and 
            calculate relevance scores for each document.
          </div>
        </div>
      </div>
    </div>
  );
}